import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import { Box, Flex, Button, Modal } from "orcs-design-system";
import { isEmpty } from "lodash";
import moment from "moment";

import { addMembershipOnly } from "src/queries/memberships.graphql";
import { changePersonSupplyGroup } from "src/allocation/graphql/person.graphql";
import AllocationPersonSearch from "src/components/AllocationPersonSearch";
import ErrorNotification from "src/components/ErrorNotification";
import PersonAllocationsModal from "src/components/Person/AllocationsModal";
import GroupPropType from "src/custom-prop-types/group";
import {
  useGroupTypes,
  useWorkspace,
} from "src/contexts/global/WorkspaceContext";
import { useAllocationProject } from "src/contexts/global/GlobalContext";
import { useUpdateTeamAndMemberDetails } from "src/hooks/useUpdateTeamAndMemberDetails";
import { useModal } from "src/contexts/withModal";
import { copywriting } from "src/pages/PersonDetailPage/PersonDetailPage.config";
import { API_DATE_FORMAT } from "src/consts/global";
import {
  MEMBERSHIP_EVENTS,
  MEMBERSHIP_CHANGE_TYPES,
} from "src/components/Person/events";
import { getSupplyGroup } from "src/util/group";
import { getGroupType } from "src/util/groupType";
import { getFteSumInput, getRemainingFte } from "src/util/fteUtil";
import eventEmitter from "src/services/eventEmitter";
import {
  useVacantRolePresetOption,
  ADD_VACANT_ROLE_OPTION_ID,
} from "src/hooks/useVacantRolePresetOption";
import AddVacantRoleModal from "src/components/AddVacantRoleModal";
import AddVacantRoleForGroupModal from "src/components/AddVacantRoleForGroupModal";

const AddMemberToTeamModal = ({ group, isVisible, onHideModal }) => {
  const [, dispatch] = useModal();
  const groupTypes = useGroupTypes();
  const workspace = useWorkspace();
  const allocationProject = useAllocationProject();
  const { presetOptions } = useVacantRolePresetOption();

  const [allocateToNewGroupMutation] = useMutation(addMembershipOnly);
  const [changeSupplyGroupMutation, { error: changeSupplyGroupError }] =
    useMutation(changePersonSupplyGroup);

  const updateTeamAndMemberDetails = useUpdateTeamAndMemberDetails();
  const [anyError, setAnyError] = useState(null);

  const onEditingPersonAllocations = useCallback(
    ({ person, teamToAdd, teamToAddError }) => {
      const onHidePersonModal = () => {
        dispatch({ type: "setModalVisible", isModalVisible: false });
      };
      const personForUpdate = { ...person };

      const modalComponent = (
        <PersonAllocationsModal
          isVisible={true}
          person={personForUpdate}
          workspace={workspace}
          groupTypes={groupTypes}
          allocationProject={allocationProject}
          onHideModal={onHidePersonModal}
          showAlreadyAllocatedNotification={true}
          forceUpdatePerson
          teamToAdd={teamToAdd}
          teamToAddError={teamToAddError}
        />
      );
      dispatch({ type: "setModalComponent", modalComponent });
    },
    [allocationProject, workspace, dispatch, groupTypes]
  );

  const updateSupplyGroup = useCallback(
    async (person) => {
      const variables = {
        personId: person.aggregateId,
        groupId: group.id,
      };

      const originalSupplyGroup = getSupplyGroup({ person, groupTypes });
      try {
        await changeSupplyGroupMutation({
          variables,
          update() {
            updateTeamAndMemberDetails({
              person,
              groups: [group, originalSupplyGroup],
              targetGroup: group,
              fteSumInput: getFteSumInput(group, groupTypes),
            })
              .then(() => {
                eventEmitter.emit(MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED, {
                  type: MEMBERSHIP_CHANGE_TYPES.ADD,
                  person,
                  targetTeamId: group.id,
                });
              })
              .catch((error) => {
                setAnyError(error);
              });
          },
        });
      } catch (error) {
        setAnyError(error);
      }
    },
    [changeSupplyGroupMutation, groupTypes, group, updateTeamAndMemberDetails]
  );

  const allocateToGroup = useCallback(
    async (person) => {
      const variables = {
        input: {
          startDate: moment().format(API_DATE_FORMAT),
          groupId: group.id,
          personId: person.aggregateId,
          fte: getRemainingFte({
            allocations: person.allocations,
            totalFte: person.fte,
          }),
        },
      };

      const fteSumInput = getFteSumInput(group, groupTypes);
      await allocateToNewGroupMutation({
        variables,
        update: () => {
          updateTeamAndMemberDetails({
            person,
            groups: [group],
            targetGroup: group,
            fteSumInput,
          })
            .then(() => {
              const hasExistingAllocations = !isEmpty(person.allocations);
              if (hasExistingAllocations) {
                onEditingPersonAllocations({ person });
              }
              eventEmitter.emit(MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED, {
                type: MEMBERSHIP_CHANGE_TYPES.ADD,
                person,
                targetTeamId: group.id,
              });
            })
            .catch((error) => {
              setAnyError(error);
            });
        },
        onError: (error) => {
          onEditingPersonAllocations({
            person,
            teamToAdd: group,
            teamToAddError: error,
          });
        },
      });
    },
    [
      allocateToNewGroupMutation,
      groupTypes,
      onEditingPersonAllocations,
      group,
      updateTeamAndMemberDetails,
    ]
  );

  const addNewVacantRole = useCallback(() => {
    const onHidePersonModal = () => {
      dispatch({ type: "setModalVisible", isModalVisible: false });
    };

    const groupType = getGroupType(group, groupTypes);

    let modalComponent;

    if (groupType?.isSupply) {
      modalComponent = (
        <AddVacantRoleForGroupModal
          group={group}
          isVisible={true}
          onHideModal={onHidePersonModal}
        />
      );
    } else {
      modalComponent = (
        <AddVacantRoleModal
          isVisible={true}
          targetGroupId={group.id}
          groupTypesLookup={groupTypes}
          onHideModal={onHidePersonModal}
        />
      );
    }
    dispatch({ type: "setModalComponent", modalComponent });
  }, [group, groupTypes, dispatch]);

  const onPersonSelected = useCallback(
    async (value) => {
      if (!value) {
        return;
      }

      if (value.id === ADD_VACANT_ROLE_OPTION_ID) {
        addNewVacantRole();
        return;
      }

      const { person, alreadyInGroup } = value;

      if (person) {
        setAnyError(null);

        if (!alreadyInGroup) {
          const groupType = getGroupType(group, groupTypes);

          try {
            if (groupType && groupType.isSupply) {
              await updateSupplyGroup(person);
            } else {
              await allocateToGroup(person);
            }
          } catch (error) {
            setAnyError(error);
          }
        }
      }
    },
    [group, groupTypes, allocateToGroup, updateSupplyGroup, addNewVacantRole]
  );

  const isAllocationAllowed =
    !groupTypes?.[group?.type]?.disableToHaveDirectReports;

  const displayError = anyError || changeSupplyGroupError;

  return (
    <Modal
      data-testid="add-member-to-team-modal"
      visible={isVisible}
      height="80vh"
      width="80vw"
      maxHeight="90vh"
      maxWidth="90vw"
      onClose={onHideModal}
      headerContent={
        <h3>
          Add member to <strong>{group?.name}</strong>
        </h3>
      }
      footerContent={
        <Flex alignItems="center" justifyContent="flex-end" width="100%">
          <Button onClick={onHideModal} mr="r">
            Done
          </Button>
        </Flex>
      }
    >
      <Box mt="s">
        <AllocationPersonSearch
          targetGroupId={group?.id}
          existingMembers={[]}
          onPersonSelected={onPersonSelected}
          groupTypes={groupTypes}
          disabled={!isAllocationAllowed}
          allocatedOptionLabel={copywriting.Allocated}
          allocateOptionLabel={copywriting.Allocate}
          includePlaceholderRoles={true}
          presetOptions={presetOptions}
        />
        {displayError && (
          <Box mt="l">
            <ErrorNotification
              onDismiss={() => setAnyError(null)}
              message={displayError?.message}
              error={displayError}
              closable={true}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
};

AddMemberToTeamModal.propTypes = {
  group: GroupPropType.isRequired,
  isVisible: PropTypes.bool.isRequired,
  onHideModal: PropTypes.func.isRequired,
};

export default AddMemberToTeamModal;
